<template>
  <div class="flex flex-col gap-4">
    <div class="flex flex-col gap-3 p-4" :class="styles.controls">
      <div class="relative w-full">
        <i
          class="fa fa-search pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-400"
        ></i>
        <input
          v-model="searchTerm"
          type="text"
          :placeholder="translations.search_by || 'Search'"
          class="w-full rounded-md border-0 py-2 pl-9 pr-3 text-sm font-medium text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 placeholder:font-normal focus:outline-none focus:ring-2 focus:ring-blue-500"
          @input="onInputChange"
        />
      </div>

      <div v-if="filters" class="flex flex-wrap items-center gap-3">
        <div
          v-for="filter in filters"
          :key="filter.name"
          class="min-w-[180px] flex-1 text-sm"
        >
          <select
            class="filter w-full rounded-md"
            data-controller="tom-select"
            :data-filter-key="filter.key"
            :placeholder="filter.name"
            @change="(value) => processSelect(filter, value)"
          >
            <option value=""></option>
            <option
              v-for="option in filter.options"
              :key="option[0]"
              :value="option[1]"
            >
              {{ option[0] }}
            </option>
          </select>
        </div>
        <button
          v-if="!styles.hide_clear_filters"
          type="button"
          class="inline-flex shrink-0 cursor-pointer items-center gap-1.5 rounded-md bg-white px-3 py-2 text-sm font-semibold text-blue-700 ring-1 ring-inset ring-blue-300 transition-colors hover:bg-blue-50"
          @click="clearFilters"
        >
          <i class="fa fa-xmark text-xs"></i>
          Clear Filters
        </button>
      </div>
    </div>
    <div class="overflow-hidden rounded-lg" :class="styles.container">
      <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200">
          <thead :class="[styles.header, 'bg-gray-50']">
            <tr
              v-for="headerGroup in table.getHeaderGroups()"
              :key="headerGroup.id"
            >
              <th
                v-for="header in headerGroup.headers"
                :key="header.id"
                :colSpan="header.colSpan"
                class="whitespace-nowrap px-4 py-3.5 text-left text-sm font-semibold text-gray-900 first:pl-6 last:pr-6 last:text-right"
                :class="
                  header.column.getCanSort()
                    ? 'cursor-pointer select-none hover:text-blue-700'
                    : ''
                "
                @click="changeSort(header)"
              >
                <FlexRender
                  v-if="!header.isPlaceholder"
                  :render="header.column.columnDef.header"
                  :props="header.getContext()"
                />

                <i
                  v-if="header.id == sortKey && sortDirection == 'asc'"
                  class="fa fa-angle-up ml-2 text-blue-600"
                ></i>
                <i
                  v-if="header.id == sortKey && sortDirection == 'desc'"
                  class="fa fa-angle-down ml-2 text-blue-600"
                ></i>
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100 bg-white">
            <tr
              v-for="row in table.getRowModel().rows"
              :key="row.id"
              :class="[
                styles.rowStyles || styles.row_styles || 'border-b',
                row.depth > 0 ? subRowStyles : '',
              ]"
            >
              <td
                v-for="cell in row.getVisibleCells()"
                :key="cell.id"
                class="whitespace-nowrap px-4 py-4 text-sm text-gray-600 first:pl-6 first:font-medium first:text-blue-700 last:pr-6 last:font-medium"
              >
                <FlexRender
                  :render="cell.column.columnDef.cell"
                  :props="cell.getContext()"
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <RemotePagination
      :pagination="pagination"
      :current-page="currentPage"
      :total-pages="totalPages"
      :total-count="totalCount"
      :translations="translations"
      @getPage="getPage"
      @handlePageSizeChange="handlePageSizeChange"
    />
  </div>
</template>

<script>
import {
  FlexRender,
  getCoreRowModel,
  useVueTable,
  createColumnHelper,
  getPaginationRowModel,
  getSortedRowModel,
} from "@tanstack/vue-table";
import { ref, h } from "vue";
import _ from "lodash";
import RemotePagination from "./RemotePagination.vue";
export default {
  components: { FlexRender, RemotePagination },
  props: {
    model: String,
    url: String,
    filters: [],
    columns: [],
    styles: {
      type: Object,
      default: () => ({}),
    },
    subRowStyles: {
      type: String,
    },
    bodyThStyles: {
      type: String,
    },
    bodyTdStyles: {
      type: String,
    },
    translations: {
      type: Object,
      required: false,
      default: () => ({}),
    },
  },
  data() {
    return {
      table: useVueTable({
        get data() {
          return [];
        },
        columns: [],
        getCoreRowModel: getCoreRowModel(),
      }),
      tableData: ref([]),
      goToPageNumber: ref(1),
      tsorting: [{ value: {} }],
      componentKey: 0,
      componentIndex: 0,
      globalFilter: "",
      modelValue: null,
      pagination: { pageIndex: 0, pageSize: 25 },
      payload: [],
      currentPage: 1,
      totalPages: 1,
      totalCount: 0,
      selectFilters: {},
      searchText: "",
      sortKey: null,
      sortDirection: "asc",
      externalFilters: {},
    };
  },
  computed: {},
  beforeMount() {
    this.loadData().then((payload) => {
      this.totalPages = payload.totalPages;
      this.totalCount = payload.totalCount;
      this.tableData = payload.data;
      this.table = this.renderTable();
    });
  },
  mounted() {
    window.addEventListener(
      "reporting-filters:changed",
      this.handleExternalFiltersChanged,
    );
  },
  beforeUnmount() {
    window.removeEventListener(
      "reporting-filters:changed",
      this.handleExternalFiltersChanged,
    );
  },
  methods: {
    handleExternalFiltersChanged(event) {
      this.externalFilters = event.detail || {};
      this.resetCurrentPage();
      this.refreshData();
    },
    changeSort(e) {
      if (!e.column.getCanSort()) {
        return;
      }
      this.sortKey = e.column.columnDef.raw || e.id;
      this.sortDirection = this.sortDirection == "asc" ? "desc" : "asc";

      this.refreshData();
    },
    onInputChange: _.debounce(function (event) {
      this.searchText = event.target.value;
      this.resetCurrentPage();

      this.refreshData();
    }, 500),
    resetCurrentPage() {
      this.currentPage = 1;
    },
    clearFilters() {
      this.selectFilters = {};
      document.querySelectorAll(".filter").forEach((filter) => {
        if (filter.tomselect) {
          filter.tomselect.setValue("null");
        } else {
          filter.selectedIndex = 0;
        }
      });
      this.refreshData();
    },
    getPage(index) {
      this.currentPage = index;
      this.refreshData().then((payload) => {
        this.tableData = payload.data;
        this.table = this.renderTable();
      });
    },
    handleColumnType(column, info) {
      let html = "";
      switch (column.column_type) {
        case "icon":
          html = `
            <icon class="${column.options.class}"/>
            `;
          return h("span", { innerHTML: html });
          break;
        case "icon_link":
          html = `
            <a href="${info.row.original[column.options.attribute] || info.row.original.url}">
              <icon class="${column.options.class}"/>
            </a>
            `;
          return h("span", { innerHTML: html });
          break;
        case "link":
          html = `
            <a href="${info.row.original.url}" class="text-blue-700 hover:text-blue-900 hover:underline">
              ${info.getValue()}
            </a>
            `;
          return h("span", { innerHTML: html });
          break;
        case "currency":
          html = new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "USD",
          }).format(info.getValue());
          return h("span", { innerHTML: html });
          break;
        default:
          return h("span", { innerHTML: info.getValue() });
      }
    },
    renderColumns() {
      const columnHelper = createColumnHelper();
      return this.columns.map((column) => {
        return columnHelper.accessor(column.key, {
          header: column.display,
          cell: (info) => this.handleColumnType(column, info),
          footer: (props) => props.column.id,
          raw: column.raw,
          enableSorting: column.sort,
        });
      });
    },
    handlePageSizeChange(e) {
      this.pagination.pageSize = e.target.value;

      this.refreshData();
    },
    handleGoToPage(e) {
      const page = e.target.value ? Number(e.target.value) - 1 : 0;
      this.goToPageNumber.value = page + 1;
      this.table.setPageIndex(page);
    },
    renderTable() {
      let that = this;
      return useVueTable({
        get data() {
          return that.tableData;
        },
        columns: this.renderColumns(),
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        manualFiltering: true,
        manualPagination: true,
        debugTable: true,
      });
    },
    processSelect(choice, event) {
      let selected = event.target.options[event.target.selectedIndex].value;

      if (selected && selected !== "null") {
        this.selectFilters[choice.key] = selected;
      } else {
        delete this.selectFilters[choice.key];
      }

      this.resetCurrentPage();
      this.refreshData();
    },
    refreshData() {
      return new Promise((resolve) => {
        let that = this;
        var url = `${this.url}.json?q=1`;
        if (this.searchText && this.searchText !== "") {
          url += `&search=${this.searchText}`;
        }
        if (this.currentPage) {
          url += `&page=${this.currentPage}`;
        }
        for (let key in this.selectFilters) {
          url += `&filter[${key}]=${this.selectFilters[key]}`;
        }
        for (let key in this.externalFilters) {
          const value = this.externalFilters[key];
          if (value !== undefined && value !== null && value !== "") {
            url += `&${key}=${encodeURIComponent(value)}`;
          }
        }
        if (this.sortKey && this.sortDirection) {
          url += `&sort_key=${this.sortKey}&sort_direction=${this.sortDirection}`;
        }
        if (this.pagination && this.pagination.pageSize) {
          url += `&page_size=${this.pagination.pageSize}`;
        }
        fetch(url)
          .then((response) => response.json())
          .then((json) => {
            that.totalPages = json.pagination.last;
            that.totalCount = json.pagination.count;

            that.tableData = json[that.model];
          })
          .catch((e) => {
            console.log("Request failure. Please contact support.");
          });
      });
    },
    loadData() {
      return new Promise((resolve) => {
        let that = this;

        var url = `${this.url}.json?page=${this.currentPage}`;
        fetch(url)
          .then((response) => response.json())
          .then((json) => {
            that.totalPages = json.pagination.last;
            that.totalCount = json.pagination.count;

            resolve({
              data: json[this.model],
              totalCount: that.totalCount,
              totalPages: that.totalPages,
            });
          })
          .catch((e) => {
            console.log("Request failure. Please contact support.");
          });
      });
    },
  },
};
</script>
